import React, { useRef, useState, useEffect } from 'react';
import getStroke from 'perfect-freehand';
import { API_BASE } from '../config';
import AddressModal from './AddressModal';

export default function SignaturePage() {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [address, setAddress] = useState(null);
  const [points, setPoints] = useState([]);
  const [allStrokes, setAllStrokes] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [meRes, sigRes] = await Promise.all([
          fetch(`${API_BASE}/auth/me`, { credentials: 'include' }),
          fetch(`${API_BASE}/signature/status`, { credentials: 'include' }),
        ]);
        if (mounted) {
          if (meRes.ok) {
            const me = await meRes.json().catch(()=>({}));
            setUserName(me?.name || '');
            setUserEmail(me?.email || '');
          }
          if (sigRes.ok) {
            const sig = await sigRes.json().catch(()=>({}));
            setAddress(sig?.address || null);
          }
        }
      } catch (_) {
        // silent
      }
    })();
    return () => { mounted = false; };
  }, []);

  const startPointer = (e) => {
    e.preventDefault();
    try { canvasRef.current.setPointerCapture?.(e.pointerId); } catch (_) {}
    setIsDrawing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setPoints([{ x, y, pressure: e.pressure || 0.5 }]);
  };
  const movePointer = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setPoints((prev) => [...prev, { x, y, pressure: e.pressure || 0.5 }]);
  };
  const endPointer = (e) => {
    e?.preventDefault?.();
    if (isDrawing && points.length > 0) setAllStrokes((prev) => [...prev, points]);
    setPoints([]);
    setIsDrawing(false);
  };

  // Draw strokes live on canvas using perfect-freehand polygons
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const options = { size: 6, thinning: 0.6, smoothing: 0.6, streamline: 0.5, start: { taper: 0 }, end: { taper: 0 } };

    const drawPoly = (poly) => {
      if (!poly || poly.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(poly[0][0], poly[0][1]);
      for (let i = 1; i < poly.length; i++) {
        ctx.lineTo(poly[i][0], poly[i][1]);
      }
      ctx.closePath();
      ctx.fillStyle = '#000';
      ctx.fill();
    };

    for (const s of allStrokes) {
      const poly = getStroke(s, options);
      drawPoly(poly);
    }
    if (points.length) {
      const poly = getStroke(points, options);
      drawPoly(poly);
    }
  }, [points, allStrokes]);

  const clear = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setPoints([]);
    setAllStrokes([]);
  };

  function getSvgPathFromStroke(stroke) {
    if (!stroke || !stroke.length) return '';
    const d = stroke.reduce((acc, [x0, y0], i, arr) => {
      const [x1, y1] = arr[(i + 1) % arr.length];
      acc.push(i === 0 ? `M ${x0} ${y0}` : '', `Q ${(x0 + x1) / 2} ${(y0 + y1) / 2} ${x1} ${y1}`);
      return acc;
    }, []).join(' ');
    return `${d} Z`;
  }

  const buildSvgPath = () => {
    const combo = points.length ? [...allStrokes, points] : allStrokes;
    if (!combo.length) return '';
    const paths = [];
    for (const s of combo) {
      const strokePoly = getStroke(s, { size: 6, thinning: 0.6, smoothing: 0.6, streamline: 0.5, start: { taper: 0 }, end: { taper: 0 } });
      if (!strokePoly || strokePoly.length < 2) continue;
      const svg = getSvgPathFromStroke(strokePoly);
      if (svg) paths.push(svg);
    }
    return paths.join(' ');
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const svgPath = buildSvgPath();
      if (!svgPath) {
        setError('Please provide your signature in the box before submitting.');
        setSubmitting(false);
        return;
      }
      const res = await fetch(`${API_BASE}/signature`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ svgPath }), credentials: 'include' });
      const data = await res.json().catch(()=>({}));
      if (res.ok) {
        setOk(true);
        setTimeout(()=>{ window.history.pushState({}, '', '/downloads'); window.dispatchEvent(new PopStateEvent('popstate')); }, 800);
      } else {
        if (data && data.error === 'address_incomplete') {
          setError('Your billing address is incomplete. Please ensure street, postal code, and country are provided and try again.');
          setShowAddressModal(true);
        } else if (data && data.error === 'signature_required') {
          setError('Please provide your signature in the box before submitting.');
        } else {
          setError('Could not save your signature. Please try again.');
        }
      }
    } catch (_) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ marginTop: 20 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>Sign digital delivery terms</h2>
      <p style={{ fontSize: 13, lineHeight: '1.5', marginBottom: 12 }}>
        I request immediate access to the digital content (downloads and/or private community). I understand and agree that once the download starts or access to the private community is granted, my statutory right of withdrawal/cancellation no longer applies. By signing this, I agree to all the above without conditions.
      </p>
      <p style={{ fontSize: 12, lineHeight: '1.4', margin: '6px 0 12px 0' }}>
        {(() => {
          const a = address || {};
          const addrLine = [a.line1, a.line2, a.postal_code, a.city || a.town || a.state, a.country].filter(Boolean).join(', ');
          const who = (userName || userEmail || '').trim() || 'the undersigned';
          const where = addrLine || 'your billing address';
          return `By signing this, I, ${who}, living at ${where}, agree to all the above without conditions.`;
        })()}
      </p>
      <div style={{ border: '1px solid #d0d7de', background: '#fff', display: 'inline-block' }}>
        <canvas
          ref={canvasRef}
          width={600}
          height={200}
          style={{ touchAction: 'none' }}
          onPointerDown={startPointer}
          onPointerMove={movePointer}
          onPointerUp={endPointer}
          onPointerLeave={endPointer}
        />
      </div>
      <div style={{ marginTop: 10 }}>
        <button className="btn" onClick={clear} disabled={submitting}>Clear</button>
        <button className="btn" onClick={submit} disabled={submitting} style={{ marginLeft: 8 }}>Submit</button>
      </div>
      {error && <div style={{ color: '#a33', marginTop: 8 }}>{error}</div>}
      {ok && <div style={{ color: 'green', marginTop: 8 }}>Saved. Redirecting…</div>}
      <AddressModal open={showAddressModal} onClose={()=>setShowAddressModal(false)} onSaved={()=>{ setShowAddressModal(false); }} />
    </div>
  );
}


